import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import CheckoutStepper from 'components/UI/CheckoutStepper';
import CheckoutHeader from './CheckoutHeader';
import PaymentSummaryCard, { summaryShape } from './PaymentSummaryCard';
import './styles.scss';

/**
 * Page shell for the contract payment checkout: mobile header + stepper band,
 * desktop checkout header and sticky summary sidebar.
 */
const PaymentCheckout = ({
  children,
  title = '',
  onBack = null,
  steps = [],
  currentStep = 0,
  showStepper = true,
  summary = null,
}) => {
  const topRef = useRef(null);
  const contentRef = useRef(null);
  const isFirstRender = useRef(true);

  // Each step starts at the top with focus on its content, so the user lands on the
  // stepper/heading instead of wherever the previous (longer) step was scrolled to.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    topRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    contentRef.current?.focus({ preventScroll: true });
  }, [currentStep, showStepper]);

  return (
    <Layout
      title={title}
      onBack={onBack}
      className='payment-checkout'
      desktopHeader={<CheckoutHeader />}
    >
      <AppShell variant='checkout'>
        <div ref={topRef} className='lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start lg:gap-10 lg:py-8 scroll-mt-24 lg:scroll-mt-28'>
          <aside className='hidden lg:block lg:sticky lg:top-[112px]'>
            <PaymentSummaryCard summary={summary} />
          </aside>
          <div className='min-w-0'>
            {showStepper && steps.length > 0 && (
              <div className='payment-checkout__stepper pb-4 pt-4 lg:pt-0 lg:pb-6'>
                <CheckoutStepper steps={steps} current={currentStep} />
              </div>
            )}
            <div
              ref={contentRef}
              tabIndex={-1}
              data-testid='checkout-content'
              aria-label={title || undefined}
              className='px-4 py-5 outline-none lg:p-0'
            >
              {children}
            </div>
          </div>
        </div>
      </AppShell>
    </Layout>
  );
};

PaymentCheckout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  onBack: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  steps: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
  })),
  currentStep: PropTypes.number,
  showStepper: PropTypes.bool,
  summary: summaryShape,
};

export default PaymentCheckout;
